import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import apiService from '../services/api';
import type { UnassignedBoat, CaptainCreateResponse } from '../types';

/**
 * 사장님 신규 계정 생성.
 * 입력: 아이디(관리자 지정), 이름, 이메일(선택), 담당 배 (다중 선택)
 * 응답: 초기 비밀번호는 이 화면에서만 노출 → 복사해서 사장님에게 카톡 전달
 */
/** Autocomplete value 는 UnassignedBoat (기존 배) or string (신규 입력) 혼재 가능. */
type BoatChoice = UnassignedBoat | string;

export default function CaptainNew() {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  // 선택된 배 목록 — 기존 배(id 있음) 와 신규 입력(문자열) 이 혼재
  const [selectedBoats, setSelectedBoats] = useState<BoatChoice[]>([]);
  const [boatOptions, setBoatOptions] = useState<UnassignedBoat[]>([]);
  const [boatSearch, setBoatSearch] = useState('');
  const [loadingBoats, setLoadingBoats] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<CaptainCreateResponse | null>(null);

  // 배 검색 (debounce)
  useEffect(() => {
    const timer = setTimeout(async () => {
      setLoadingBoats(true);
      try {
        const boats = await apiService.getUnassignedBoats({
          search: boatSearch.trim() || undefined,
          limit: 30,
        });
        setBoatOptions(boats);
      } catch {
        setBoatOptions([]);
      } finally {
        setLoadingBoats(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [boatSearch]);

  const canSubmit =
    username.trim().length >= 3 &&
    name.trim().length >= 1 &&
    selectedBoats.length >= 1 &&
    !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      // 기존 배 (object) 와 신규 입력 (string) 분리
      const boatIds: number[] = [];
      const newBoatNames: string[] = [];
      for (const b of selectedBoats) {
        if (typeof b === 'string') {
          const trimmed = b.trim();
          if (trimmed) newBoatNames.push(trimmed);
        } else {
          boatIds.push(b.id);
        }
      }
      const res = await apiService.createCaptain({
        username: username.trim(),
        name: name.trim(),
        email: email.trim() || undefined,
        phone: phone.trim() || undefined,
        boatIds,
        newBoatNames,
      });
      setResult(res);
    } catch (e: any) {
      setError(e?.response?.data?.message ?? '계정 생성 실패');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    alert('클립보드에 복사됨');
  };

  const handleCopyAll = () => {
    if (!result) return;
    const text = `[어랑 사장님 로그인 안내]
아이디: ${result.username}
비밀번호: ${result.initialPassword}

첫 로그인 후 비밀번호를 변경해주세요.`;
    navigator.clipboard.writeText(text);
    alert('전체 안내문이 클립보드에 복사됨');
  };

  // 결과 화면 (계정 생성 성공 후)
  if (result) {
    return (
      <Box sx={{ p: 3, maxWidth: 700, mx: 'auto' }}>
        <Paper sx={{ p: 4 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
            <Typography variant="h5" fontWeight="bold" color="success.main">
              ✅ 계정 생성 완료
            </Typography>
          </Stack>

          <Alert severity="warning" sx={{ mb: 3 }}>
            <strong>초기 비밀번호는 이 화면에서만 확인 가능합니다.</strong>
            <br />
            지금 복사해서 사장님께 카톡·문자로 전달해주세요.
          </Alert>

          <Stack spacing={2}>
            <Box>
              <Typography variant="body2" color="text.secondary">
                이름
              </Typography>
              <Typography variant="h6">{result.name}</Typography>
            </Box>

            <Box>
              <Typography variant="body2" color="text.secondary">
                아이디
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h6" fontFamily="monospace">
                  {result.username}
                </Typography>
                <IconButton size="small" onClick={() => handleCopy(result.username)}>
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>

            <Box>
              <Typography variant="body2" color="text.secondary">
                초기 비밀번호
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  p: 2,
                  bgcolor: 'grey.100',
                  borderRadius: 1,
                }}
              >
                <Typography
                  variant="h5"
                  fontFamily="monospace"
                  sx={{ flex: 1, letterSpacing: 2 }}
                >
                  {result.initialPassword}
                </Typography>
                <IconButton
                  onClick={() => handleCopy(result.initialPassword)}
                  color="primary"
                >
                  <ContentCopyIcon />
                </IconButton>
              </Box>
            </Box>

            <Box>
              <Typography variant="body2" color="text.secondary">
                담당 배 ({result.boatIds.length}척)
              </Typography>
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                {selectedBoats.map((b, i) => {
                  const isNew = typeof b === 'string';
                  return (
                    <Chip
                      key={isNew ? `new-${i}` : b.id}
                      label={isNew ? `${b} (신규)` : b.name}
                      size="small"
                      color={isNew ? 'success' : 'primary'}
                      variant={isNew ? 'outlined' : 'filled'}
                    />
                  );
                })}
              </Stack>
            </Box>
          </Stack>

          <Divider sx={{ my: 3 }} />

          <Stack direction="row" spacing={1} justifyContent="space-between">
            <Button
              variant="outlined"
              startIcon={<ContentCopyIcon />}
              onClick={handleCopyAll}
            >
              전체 안내문 복사
            </Button>
            <Stack direction="row" spacing={1}>
              <Button
                variant="text"
                onClick={() => {
                  setResult(null);
                  setUsername('');
                  setName('');
                  setEmail('');
                  setPhone('');
                  setSelectedBoats([]);
                }}
              >
                다른 사장님 추가
              </Button>
              <Button
                variant="contained"
                onClick={() => navigate('/captains')}
              >
                목록으로
              </Button>
            </Stack>
          </Stack>
        </Paper>
      </Box>
    );
  }

  // 입력 화면
  return (
    <Box sx={{ p: 3, maxWidth: 700, mx: 'auto' }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
        <IconButton onClick={() => navigate('/captains')}>
          <ArrowBackIcon />
        </IconButton>
        <PersonAddIcon color="primary" />
        <Typography variant="h5" fontWeight="bold">
          새 사장님 계정 생성
        </Typography>
      </Stack>

      <Paper sx={{ p: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Stack spacing={3}>
          <TextField
            label="아이디 *"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            fullWidth
            helperText="사장님이 로그인할 아이디. 영문·숫자·언더바 권장. 예: kimboat, hae_captain"
            InputProps={{ style: { fontFamily: 'monospace' } }}
          />

          <TextField
            label="사장님 이름 *"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            helperText="예: 김선장, 이대표"
          />

          <TextField
            label="이메일 (선택)"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            type="email"
            fullWidth
          />

          <TextField
            label="전화번호 (선택)"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            fullWidth
            helperText="관리자 참조용. 사장님과의 카톡·문자용"
          />

          <Divider>담당 배 선택 *</Divider>

          <Autocomplete
            multiple
            freeSolo
            options={boatOptions}
            value={selectedBoats}
            onChange={(_, val) => setSelectedBoats(val as BoatChoice[])}
            getOptionLabel={(o) =>
              typeof o === 'string'
                ? o
                : `${o.name} (${o.homePort ?? o.region ?? '지역 미상'})`
            }
            isOptionEqualToValue={(a, b) => {
              if (typeof a === 'string' || typeof b === 'string') return a === b;
              return a.id === b.id;
            }}
            loading={loadingBoats}
            onInputChange={(_, val) => setBoatSearch(val)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="배 이름"
                placeholder="검색 or 직접 입력 후 Enter"
                helperText="목록에서 선택하거나, 없는 배는 직접 입력 후 Enter — 신규 배로 자동 등록됨"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingBoats && <CircularProgress size={18} />}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => {
                const isNew = typeof option === 'string';
                return (
                  <Chip
                    {...getTagProps({ index })}
                    key={isNew ? `new-${option}` : option.id}
                    label={
                      isNew ? `${option} (신규)` : option.name
                    }
                    color={isNew ? 'success' : 'primary'}
                    variant={isNew ? 'outlined' : 'filled'}
                  />
                );
              })
            }
          />
        </Stack>

        <Divider sx={{ my: 3 }} />

        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button
            variant="text"
            onClick={() => navigate('/captains')}
            disabled={submitting}
          >
            취소
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!canSubmit}
            startIcon={submitting ? <CircularProgress size={18} /> : <PersonAddIcon />}
          >
            {submitting ? '생성 중...' : '계정 생성'}
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
}
